import { PRODUCTS, type Product } from '@noconflict/contracts';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderErrorMessage } from '../../../entities/order';
import { useStartOrder } from '../../../entities/project';
import { Badge } from '../../../shared/ui/badge';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';

/** Products whose order form exists. Any other would be shown as coming soon. */
const AVAILABLE: readonly Product[] = ['title', 'script'];

/**
 * **New order** in a workspace: a choice between the products, each with what it is, then `start_order`, which
 * creates the project as a draft, and on to its form. The caller decides whether to show it (`orders.create` and
 * `projects.create` in an unarchived workspace); the RPC refuses regardless.
 */
export function StartOrderButton({
  workspaceId,
  onStarted,
}: {
  workspaceId: string;
  /** Where to go next: the new project's id. */
  onStarted: (projectId: string) => void;
}) {
  const { t } = useTranslation('order');
  const start = useStartOrder(workspaceId);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(product: Product) {
    setError(null);
    try {
      const projectId = await start.mutateAsync(product);
      setOpen(false);
      onStarted(projectId);
    } catch (e) {
      setError(orderErrorMessage(e));
    }
  }

  return (
    <>
      <Button
        variant="accent"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        <PlusIcon aria-hidden="true" />
        {t('start.open')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('start.title')}</DialogTitle>
            <DialogDescription>{t('start.help')}</DialogDescription>
          </DialogHeader>
          <ul className="grid gap-3">
            {PRODUCTS.map((product) => {
              const available = AVAILABLE.includes(product);
              return (
                <li key={product} className="grid gap-3 rounded-sm border border-input bg-card p-4">
                  <div className="grid gap-1">
                    <p className="flex flex-wrap items-center gap-2 font-bold text-heading">
                      {t(`products.${product}`)}
                      {available ? null : <Badge variant="neutral">{t('start.comingSoon')}</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t(product === 'title' ? 'start.titleHelp' : 'start.scriptHelp')}
                    </p>
                  </div>
                  <div>
                    <Button
                      variant={available ? 'primary' : 'secondary'}
                      disabled={!available || start.isPending}
                      onClick={() => {
                        void choose(product);
                      }}
                    >
                      {start.isPending && available
                        ? t('start.starting')
                        : t(product === 'title' ? 'start.chooseTitle' : 'start.chooseScript')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
